/**
 * **************************************************************************
 * Workflow queue manager service file
 * 
 * Defines all http api calls required in workflow queue manager page
 * Calls server apis and returns data to components
 * **************************************************************************
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/services/api.service';
import { Priority } from '@app/shared/components/que-mnger-expand-config/priority';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})

export class WfQueueManagerService {

  /****************** Variable declaration ******************/

  /* define required urls */
  public featureApis = this.apiService.FEATURE_APIS;
  public serverUrl = this.apiService.KPID_URL + this.featureApis.FEATURE;
  public workflowApis = this.apiService.WORKFLOW_APIS;
  public workflowUrl = this.apiService.KPID_URL + this.workflowApis.WORKFLOW;

  public WfQueueApis = this.apiService.WF_QUEUE;
  public wfQueueUrl = this.apiService.KPID_URL + this.WfQueueApis.WFQ;

  public header = this.apiService.headers
  isHeaders = false;

  currentPage: number = 0;
  sortedData: any;
  private dataSourceLengthUpdSubject = new Subject<[string, number]>();
  dataSourceLengthUpd$ = this.dataSourceLengthUpdSubject.asObservable();
  widthFlag:Boolean = false;
  maxWidth:number =160;
  

  /**
   * Constructor
   * @param httpClient 
   * @param apiService 
   */
  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService
  ) { }

  /**
   * Retrieve the feature details
   * @param duration - duartion between which data has to be retrieved
   * @param id - id of the workflow
   * @returns 
   */
  getFeatureData(duration: String, id: number): Observable<any> {
    const url = this.serverUrl + this.featureApis.FEATURE_DATA(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve the jobs that are in queued state or in-progress state
   * @param duration - duartion between which data has to be retrieved
   * @param id - id of the workflow whose jobs has to be retrieved
   * @returns 
   */
  getQueueData(duration: String, id: number, headersVal?: boolean): Observable<any> {
    const url = this.serverUrl + this.featureApis.FEATURE_QUEUED(duration, id);
    let options = {};
    if (headersVal == true) {
      options = { headers: this.header };
    }
    let response = this.httpClient.get(url, options).pipe(map((data: any) => {

      if (data?.data?.dbResp.length > 0) {
        return data?.data?.dbResp.map((queue: any) => {
          queue.id = queue.workflowId;
          delete queue.workflowId
          return queue
        })
      }
      return [];
    }));
    return response;
  }

  /**
   * Retrieve queued/in-progress jobs statistcis
   * @param duration - duartion between which data has to be retrieved
   * @param id - id of the workflow whose jobs has to be retrieved
   * @returns 
   */
  getQueueCardsData(duration: String, id: number, headersVal?: boolean): Observable<any> {
    const url = this.serverUrl + this.featureApis.FEATURE_QUEUE_CARDS(duration, id);
    let options = {};
    if (headersVal == true) {
      options = { headers: this.header };
    }

    let response = this.httpClient.get(url, options);
    return response;
  }

  /**
   * 
   * @param duration 
   * @param id 
   * @returns 
   */
  getQueueWfDataForConfig(duration: String, id: number): Observable<any> {
    let options: any = { headers: this.header }

    const url = this.serverUrl + this.featureApis.FEATURE_QUEUED_PIPELINE_DATA_FOR_CONFIG(duration, id);
    let response = this.httpClient.get(url, options);
    return response;

  }

  /**
   * Retrieve list of available workflows
   * @param id 
   * @returns 
   */
  getQWorkflowName(): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_NAME();
    let response = this.httpClient.get(url);
    return response;
  }

  updatePriority(jobId: any, priority: Priority): Observable<any> {
    return this.httpClient.patch(this.wfQueueUrl + this.WfQueueApis.WFQ_UPDATE_PRIO(jobId, priority), {}, {
      headers: {
        'content-type': 'text/plain'
      }
    });
  }

  /**Function retrieves input arguments from wf_job_exec table when the trigger is manual workflow */
  getInputArgs(jobId: any) {
    return this.httpClient.get(this.wfQueueUrl + this.WfQueueApis.WFQ_QUEUE_INP_ARGS(jobId));
  }

  /**
   * method to abort workflow run
   *
   * @param jobId         workflow job id
   * @param status        current workflow job status
   * @param abortWorkflow WfQueue
   * @return Response
   */
  abortWorkflowJob(jobId: any, status: any, actionUrl: string): Observable<any> {
    let runId = null, repoName = null, repoOwner = null;
    if (status === 'inprogress' || status === 'dispatched') {
      let urlParts = actionUrl.split('/');
      runId = urlParts[urlParts.length - 1];
      repoName = urlParts[urlParts.length - 4];
      repoOwner = urlParts[urlParts.length - 5];
    }

    return this.httpClient.put(this.wfQueueUrl + this.WfQueueApis.WFQ_ABORT(jobId, status), { repoOwner, repoName, runId });
  }

  /**
 * reRunExisitingWf function will re run the workflow which is already executed,
 * it will re run the same workflow with same configurations
 * @param jobId 
 * @returns 
 */
  reRunExisitingWf(jobId: any) {
    let url = this.wfQueueUrl + this.WfQueueApis.EXISTING_WF_TRIGGER(jobId);
    return this.httpClient.post(url, {}, {
      headers: {
        'content-type': 'text/plain'
      }
    });
  }

  /**getCurrentPage is used to get pagination current page */
  getCurrentPage(): number {
    return this.currentPage;
  }

  /**setCurrentPage is used to set pagination current page */
  setCurrentPage(currentPage: number) {
    this.currentPage = currentPage;
  }

  /**getSortedData is used to get sorted data */
  getSortedData(): any {
    return this.sortedData;
  }

  /**setSortedData is used to set sorted data */
  setSortedData(sortData: any) {
    this.sortedData = sortData;
  }

  /** 
   * updateDatasourceLength is used to update the count of total workflows
   * whenever workflows are aborted
   */
  updateDataSourceLength(id: string, length: number) {
    this.dataSourceLengthUpdSubject.next([id, length]);
  }
}
